/* eslint-disable @typescript-eslint/no-explicit-any */
import { useNavigate, useParams } from 'react-router-dom';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Card,
  CardBody,
  CardHeader,
  Divider,
  Input,
  Select,
  SelectItem,
  addToast,
  Switch,
  DatePicker,
} from '@heroui/react';
import { useTranslation } from 'react-i18next';
import {
  FaArrowLeft,
  FaPaperPlane,
  FaPlus,
  FaMapLocationDot,
  FaRegClock,
} from 'react-icons/fa6';
import { useRef, useState } from 'react';
import { parseDate, parseDateTime } from '@internationalized/date';
import CustomButton from '../../components/CustomButton';
import CustomInput from '../../components/CustomInput';
import { FEATURE_FLAGS, useFeatureFlag } from '../../../config/featureFlags';
import CustomAlert from '../../components/CustomAlert';
import { useCreateJob } from '../../../hook/useMutationData';
import { useFetchServiceByQuery } from '../../../hook/useQueryData';
import { uploadToS3 } from '../Customer/aws/s3FileUpload';
import { TimeframePreference } from '../../../utils/type';
import { all_routes } from '../../../core/data/routes/all_routes';
import { PromptAnswer } from '../../../service/jobService';
import CustomTextArea from '../../components/common/ui/CustomTextArea';
import CustomChip from '../../components/CustomChip';
import AddOnSelector from '../../components/AddOnSelector';

interface JobRequestFormValues {
  scopeDescription: string;
  location?: string;
  timeframePreference: TimeframePreference;
  budgetMin?: string;
  budgetMax?: string;
}

interface PromptFieldState {
  [promptId: string]: string | number | boolean | string[];
}

interface ScopePromptInfo {
  promptId: string;
  label: string;
  type?: string;
  required?: boolean;
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
  isLegacy?: boolean;
}

const schema = yup.object({
  scopeDescription: yup
    .string()
    .required('Describe what you need')
    .min(20, 'Please add a bit more detail (min 20 characters)')
    .max(2000, 'Keep the description under 2000 characters'),
  location: yup.string().max(200, 'Location is too long'),
  timeframePreference: yup
    .mixed<TimeframePreference>()
    .oneOf(['asap', '2weeks', 'month', 'flexible'], 'Select a timeframe')
    .required('Select a timeframe'),
  budgetMin: yup
    .string()
    .optional()
    .test(
      'valid-budget',
      'Enter a valid number',
      (v) => !v || !Number.isNaN(Number(v))
    ),
  budgetMax: yup
    .string()
    .optional()
    .test(
      'valid-budget',
      'Enter a valid number',
      (v) => !v || !Number.isNaN(Number(v))
    )
    .test('gt-min', 'Max should be greater than min', function (v) {
      const min = this.parent?.budgetMin;
      if (!v || !min) return true;
      return Number(v) > Number(min);
    }),
});

const TIMEFRAME_OPTIONS: { value: TimeframePreference; label: string }[] = [
  { value: 'asap', label: 'As soon as possible' },
  { value: '2weeks', label: 'Within 2 weeks' },
  { value: 'month', label: 'Within a month' },
  { value: 'flexible', label: 'Flexible — let me discuss' },
];

/**
 * Phase 2 — Job Request Form (QUOTE flow entry).
 * Route: /service/request/:serviceId
 *
 * Customer describes the scope, optional photos + location, a timeframe
 * preference and optional budget range. On submit → POST /v1/job
 * (status 'Requested', bookingType 'QUOTE').
 */
const JobRequestForm = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const createJobMutation = useCreateJob();
  const [photoUrls, setPhotoUrls] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedAdditionalServiceIds, setSelectedAdditionalServiceIds] =
    useState<string[]>([]);
  const [promptFieldValues, setPromptFieldValues] = useState<PromptFieldState>(
    {}
  );
  const [scopePromptsInfo, setScopePromptsInfo] = useState<ScopePromptInfo[]>(
    []
  );

  /**
   * Kill switch for the redesigned quotation flow (PR #1482).
   *
   * ON  → HeroUI toasts.
   * OFF → the pre-#1482 blocking `window.alert`, so the form still reports
   *       every error it did before if the toast layer misbehaves live.
   *
   * Fail-open: reads ON when Unleash is unreachable or unconfigured. Only the
   * notification layer is gated — #1482's other quotation changes (CustomInput,
   * chip radius) are presentational and are NOT reverted by this flag.
   */
  const quotationV2Enabled = useFeatureFlag(FEATURE_FLAGS.quotationFlowV2);

  const { data: serviceResponse, isLoading: isServiceLoading } =
    useFetchServiceByQuery({
      data: {
        filters: { serviceId },
        fullProfile: true,
        ownerId: '',
        categoryProjection: 'categoryName',
        withMongoId: false,
      },
      enable: Boolean(serviceId),
    });

  const requestedService = serviceResponse?.services?.[0] || serviceResponse;

  /**
   * Provider response SLA (Item 1). Surfaced from the service public/customer
   * read (`respondsWithinDays`). Falls back to a sane default when the field
   * is absent so the request form always gives the customer a response-time
   * expectation.
   */
  const responseSlaDays =
    typeof requestedService?.respondsWithinDays === 'number'
      ? requestedService.respondsWithinDays
      : 2;

  const normalizeOptions = (
    options: any[] | undefined
  ): Array<{ value: string; label: string }> => {
    if (!options || options.length === 0) return [];
    return options.map((opt) => {
      if (typeof opt === 'string') {
        return { value: opt, label: opt };
      }
      return {
        value: opt.value ?? opt.label ?? String(opt),
        label: opt.label ?? opt.value ?? String(opt),
      };
    });
  };

  /**
   * Process scope prompts: convert legacy strings and structured objects into
   * ScopePromptInfo for rendering.
   */
  const buildScopePromptsInfo = (
    prompts: any[] | undefined
  ): ScopePromptInfo[] => {
    if (!prompts || prompts.length === 0) return [];
    return prompts.map((prompt, idx) => {
      if (typeof prompt === 'string') {
        // Legacy plain string: generate a stable promptId
        return {
          promptId: `legacy_${idx}`,
          label: prompt,
          type: 'SHORT_TEXT',
          required: false,
          isLegacy: true,
        };
      }
      // Structured prompt object
      return {
        promptId: prompt.promptId || `prompt_${idx}`,
        label: prompt.label || prompt.promptText || '',
        type: prompt.type || 'SHORT_TEXT',
        required: prompt.required || false,
        options: normalizeOptions(prompt.options),
        placeholder: prompt.placeholder,
        isLegacy: false,
      };
    });
  };

  // Update scopePromptsInfo whenever service changes
  if (
    requestedService?.scopePrompts &&
    scopePromptsInfo.length !==
      buildScopePromptsInfo(requestedService.scopePrompts).length
  ) {
    const newPromptsInfo = buildScopePromptsInfo(requestedService.scopePrompts);
    setScopePromptsInfo(newPromptsInfo);
    // Reset prompt field values when service changes
    setPromptFieldValues({});
  }

  const notify = (
    title: string,
    description: string,
    color: 'warning' | 'danger'
  ) => {
    if (!quotationV2Enabled) {
      window.alert(description);
      return;
    }
    addToast({ title, description, radius: 'md', color });
  };

  const form = useForm<JobRequestFormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      scopeDescription: '',
      location: '',
      timeframePreference: 'asap',
      budgetMin: '',
      budgetMax: '',
    },
  });

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const uploaded: string[] = [];
      // Upload up to 4 new photos, capped at a total of 6.
      const remaining = Math.max(0, 6 - photoUrls.length);
      const slice = Array.from(files).slice(0, remaining);
      for (const file of slice) {
        const url = await uploadToS3(file, 'job-request-photos');
        uploaded.push(url);
      }
      if (uploaded.length === 0) {
        notify(
          'Photo limit reached',
          'You can attach up to 6 photos.',
          'warning'
        );
      } else {
        setPhotoUrls((prev) => [...prev, ...uploaded].slice(0, 6));
      }
    } catch (err) {
      console.error('Photo upload failed:', err);
      notify(
        'Upload failed',
        'One or more photos failed to upload. Please try again.',
        'danger'
      );
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  /**
   * True when a required prompt has no answer yet. BOOLEAN's `false` and
   * NUMBER's `0` are real answers, not missing ones — only an actually-empty
   * string/array or an unset value count as missing.
   */
  const isPromptAnswerMissing = (prompt: ScopePromptInfo): boolean => {
    if (!prompt.required) return false;
    const value = promptFieldValues[prompt.promptId];
    if (value === undefined || value === null) return true;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'string') return value.trim().length === 0;
    return false;
  };

  const onSubmit = (values: JobRequestFormValues) => {
    if (!serviceId) {
      notify(
        'Something went wrong',
        'Service reference is missing. Please go back and try again.',
        'danger'
      );
      return;
    }

    const missingRequired = scopePromptsInfo.find(isPromptAnswerMissing);
    if (missingRequired) {
      notify(
        'Missing required answer',
        `Please answer "${missingRequired.label}" before submitting.`,
        'warning'
      );
      return;
    }

    // Build promptAnswers from collected field values. `??`, not `||` — a
    // NUMBER answer of 0 or a BOOLEAN answer of false is a real, valid typed
    // value, not a missing one; `||` would have silently replaced either
    // with an empty string.
    const promptAnswers: PromptAnswer[] = scopePromptsInfo.map((prompt) => ({
      promptId: prompt.promptId,
      label: prompt.label,
      type: prompt.type || 'SHORT_TEXT',
      answer: promptFieldValues[prompt.promptId] ?? '',
    }));

    createJobMutation.mutate(
      {
        serviceId,
        scopeDescription: values.scopeDescription,
        photos: photoUrls.length > 0 ? photoUrls : undefined,
        location: values.location?.trim() || undefined,
        timeframePreference: values.timeframePreference,
        budgetMin: values.budgetMin ? Number(values.budgetMin) : undefined,
        budgetMax: values.budgetMax ? Number(values.budgetMax) : undefined,
        additionalServiceIds:
          selectedAdditionalServiceIds.length > 0
            ? selectedAdditionalServiceIds
            : undefined,
        promptAnswers: promptAnswers.length > 0 ? promptAnswers : undefined,
      },
      {
        onSuccess: () => {
          navigate(all_routes.customerJobRequests);
        },
      }
    );
  };

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = form;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-body text-gray-500 hover:text-gray-800 mb-6 cursor-pointer transition-colors"
      >
        <FaArrowLeft size={14} /> {t('quote.back', 'Back')}
      </button>

      <div className="mb-8">
        <h1 className="text-xl font-bold text-primary">
          {t('quote.request_title', 'Request a Quote')}
        </h1>
        <p className="text-body text-gray-500 mt-1">
          {t(
            'quote.request_subtitle',
            'Tell us about your project. The provider will reply with a quote.'
          )}
        </p>
      </div>

      <FormProvider {...form}>
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_22rem] gap-8 items-start">
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Card shadow="sm" className="border border-gray-200">
              <CardHeader className="border-b border-gray-100 px-6 py-5">
                <p className="text-subtitle3 font-semibold text-gray-800">
                  {t('quote.project_details', 'Project Details')}
                </p>
              </CardHeader>
              <CardBody className="px-6 py-6 space-y-6">
                {/* What's included section */}
                {requestedService?.includes &&
                requestedService.includes.length > 0 ? (
                  <div>
                    <label className="text-body font-medium text-gray-700 block mb-1.5">
                      {t('quote.whats_included', "What's included")}
                    </label>
                    <ul className="list-disc pl-5 space-y-1 text-body text-gray-600">
                      {requestedService.includes.map(
                        (item: string, idx: number) => (
                          <li key={idx}>{item}</li>
                        )
                      )}
                    </ul>
                  </div>
                ) : null}

                {/* Optional extras section */}
                {requestedService?.additionalServices &&
                requestedService.additionalServices.length > 0 ? (
                  <div>
                    <label className="text-body font-medium text-gray-700 block mb-1.5">
                      {t('quote.optional_extras', 'Optional extras')}
                    </label>
                    <AddOnSelector
                      addons={requestedService.additionalServices}
                      selectedIds={selectedAdditionalServiceIds}
                      onChange={setSelectedAdditionalServiceIds}
                    />
                  </div>
                ) : null}

                {/* Scope prompts section - render before scopeDescription if prompts exist */}
                {scopePromptsInfo.length > 0 ? (
                  <div>
                    <label className="text-body font-medium text-gray-700 block mb-1.5">
                      {t(
                        'quote.scope_prompts_label',
                        'The provider would like to know'
                      )}
                    </label>
                    <div className="space-y-4">
                      {scopePromptsInfo.map((prompt) => (
                        <div
                          key={prompt.promptId}
                          className="flex flex-col gap-2"
                        >
                          <label className="text-body font-medium text-gray-700">
                            {prompt.label}
                            {prompt.required && (
                              <span className="text-danger ml-0.5">*</span>
                            )}
                          </label>

                          {prompt.type === 'SHORT_TEXT' ||
                          prompt.type === 'TEXT' ? (
                            <CustomInput
                              type="text"
                              placeholder={prompt.placeholder}
                              value={
                                (promptFieldValues[
                                  prompt.promptId
                                ] as string) || ''
                              }
                              onChange={(e) =>
                                setPromptFieldValues((prev) => ({
                                  ...prev,
                                  [prompt.promptId]: e.target.value,
                                }))
                              }
                              classNames={{ input: 'text-sm' }}
                            />
                          ) : prompt.type === 'LONG_TEXT' ? (
                            <CustomTextArea
                              label=""
                              placeholder={prompt.placeholder}
                              minRows={3}
                              value={
                                (promptFieldValues[
                                  prompt.promptId
                                ] as string) || ''
                              }
                              onChange={(e) =>
                                setPromptFieldValues((prev) => ({
                                  ...prev,
                                  [prompt.promptId]: e.target.value,
                                }))
                              }
                              classNames={{
                                input: 'text-sm',
                                inputWrapper: 'resize-none !bg-white',
                              }}
                            />
                          ) : prompt.type === 'NUMBER' ? (
                            <Input
                              type="number"
                              placeholder={prompt.placeholder}
                              value={String(
                                promptFieldValues[prompt.promptId] || ''
                              )}
                              onChange={(e) =>
                                setPromptFieldValues((prev) => ({
                                  ...prev,
                                  [prompt.promptId]: e.target.value
                                    ? Number(e.target.value)
                                    : '',
                                }))
                              }
                              size="sm"
                              variant="bordered"
                              classNames={{
                                input: 'text-xs bg-white',
                                inputWrapper:
                                  'border border-gray-300 rounded-md',
                              }}
                            />
                          ) : prompt.type === 'BOOLEAN' ? (
                            <Switch
                              isSelected={Boolean(
                                promptFieldValues[prompt.promptId]
                              )}
                              onChange={(e) =>
                                setPromptFieldValues((prev) => ({
                                  ...prev,
                                  [prompt.promptId]: e.target.checked,
                                }))
                              }
                            />
                          ) : prompt.type === 'SINGLE_SELECT' ? (
                            <Select
                              size="sm"
                              placeholder={
                                prompt.placeholder || 'Select an option'
                              }
                              selectedKeys={
                                promptFieldValues[prompt.promptId]
                                  ? new Set([
                                      String(
                                        promptFieldValues[prompt.promptId]
                                      ),
                                    ])
                                  : new Set()
                              }
                              onSelectionChange={(keys) => {
                                const val = Array.from(keys as Set<string>)[0];
                                setPromptFieldValues((prev) => ({
                                  ...prev,
                                  [prompt.promptId]: val || '',
                                }));
                              }}
                              classNames={{
                                trigger:
                                  'border !border-gray-300 rounded-md !bg-white',
                                value: 'text-xs',
                                listbox: 'text-xs',
                              }}
                            >
                              {prompt.options?.map((opt) => (
                                <SelectItem
                                  key={opt.value}
                                  textValue={opt.label}
                                  className="text-xs"
                                >
                                  {opt.label}
                                </SelectItem>
                              )) || []}
                            </Select>
                          ) : prompt.type === 'MULTI_SELECT' ? (
                            <Select
                              size="sm"
                              placeholder={
                                prompt.placeholder || 'Select options'
                              }
                              selectionMode="multiple"
                              selectedKeys={
                                Array.isArray(
                                  promptFieldValues[prompt.promptId]
                                )
                                  ? new Set(
                                      promptFieldValues[
                                        prompt.promptId
                                      ] as string[]
                                    )
                                  : new Set()
                              }
                              onSelectionChange={(keys) => {
                                setPromptFieldValues((prev) => ({
                                  ...prev,
                                  [prompt.promptId]: Array.from(
                                    keys as Set<string>
                                  ),
                                }));
                              }}
                              classNames={{
                                trigger:
                                  'border !border-gray-300 rounded-md !bg-white',
                                value: 'text-xs',
                                listbox: 'text-xs',
                              }}
                            >
                              {prompt.options?.map((opt) => (
                                <SelectItem
                                  key={opt.value}
                                  textValue={opt.label}
                                  className="text-xs"
                                >
                                  {opt.label}
                                </SelectItem>
                              )) || []}
                            </Select>
                          ) : prompt.type === 'DATE' ||
                            prompt.type === 'DATETIME' ? (
                            <DatePicker
                              size="sm"
                              granularity={
                                prompt.type === 'DATETIME' ? 'minute' : 'day'
                              }
                              hideTimeZone={prompt.type === 'DATETIME'}
                              value={(() => {
                                const raw = promptFieldValues[prompt.promptId];
                                if (!raw) return undefined;
                                const str = String(raw);
                                if (prompt.type === 'DATETIME') {
                                  // Try to parse as full datetime; fall back to date-only at midnight
                                  try {
                                    return parseDateTime(
                                      str.length > 10 ? str : `${str}T00:00:00`
                                    );
                                  } catch {
                                    return parseDate(str.substring(0, 10));
                                  }
                                }
                                return parseDate(str.substring(0, 10));
                              })()}
                              onChange={(date) => {
                                setPromptFieldValues((prev) => ({
                                  ...prev,
                                  [prompt.promptId]: date
                                    ? date.toString()
                                    : '',
                                }));
                              }}
                              classNames={{
                                inputWrapper:
                                  'border !border-gray-300 rounded-md !bg-white hover:!border-gray-300 focus-within:!border-gray-300',
                                input: 'text-xs bg-white',
                              }}
                            />
                          ) : (
                            // Fallback for unknown types
                            <CustomInput
                              type="text"
                              placeholder={prompt.placeholder}
                              value={
                                (promptFieldValues[
                                  prompt.promptId
                                ] as string) || ''
                              }
                              onChange={(e) =>
                                setPromptFieldValues((prev) => ({
                                  ...prev,
                                  [prompt.promptId]: e.target.value,
                                }))
                              }
                              classNames={{ input: 'text-sm' }}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div>
                  <CustomTextArea
                    {...register('scopeDescription')}
                    variant="flat"
                    label={t('quote.scope_label', 'Describe what you need')}
                    isRequired
                    minRows={5}
                    placeholder={t(
                      'quote.scope_placeholder',
                      'What work needs to be done? Include details like size, materials, timeline expectations…'
                    )}
                    isInvalid={Boolean(errors.scopeDescription)}
                    errorMessage={errors.scopeDescription?.message}
                    classNames={{
                      input: 'text-sm',
                      label: 'text-xs',
                      inputWrapper:
                        'border !border-gray-300 resize-none !bg-white hover:!border-gray-300 focus-within:!border-gray-300 focus-within:!bg-white outline-none focus:outline-none',
                    }}
                  />
                </div>

                <div>
                  <label className="text-body font-medium text-gray-700 block mb-1.5">
                    {t('quote.photos_label', 'Photos (optional)')}
                  </label>
                  <div className="flex flex-wrap gap-3 items-center">
                    {photoUrls.map((url) => (
                      <div
                        key={url}
                        className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200"
                      >
                        <img
                          src={url}
                          alt="upload"
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setPhotoUrls((prev) =>
                              prev.filter((u) => u !== url)
                            )
                          }
                          className="absolute top-1 right-1 bg-black/60 text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center hover:bg-black transition-colors"
                          aria-label="Remove photo"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    {photoUrls.length < 6 && (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400 hover:border-primary hover:text-primary transition cursor-pointer disabled:opacity-50"
                      >
                        <FaPlus size={18} />
                        <span className="text-[10px] mt-1">
                          {uploading ? '…' : 'Add'}
                        </span>
                      </button>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => handleFiles(e.target.files)}
                    />
                  </div>
                  <p className="text-caption text-gray-400 mt-1.5">
                    {t('quote.photos_hint', 'Up to 6 photos, 10MB each')}
                  </p>
                </div>

                <div>
                  <CustomInput
                    {...register('location')}
                    type="text"
                    label={t('quote.location_label', 'Location (optional)')}
                    placeholder={t(
                      'quote.location_placeholder',
                      'City, neighbourhood, or address'
                    )}
                    isInvalid={Boolean(errors.location)}
                    errorMessage={errors.location?.message}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-body font-medium text-gray-700 block mb-1.5">
                      {t('quote.timeframe_label', 'Timeframe')}
                      <span className="text-danger ml-0.5">*</span>
                    </label>
                    <Controller
                      control={control}
                      name="timeframePreference"
                      render={({ field }) => (
                        <Select
                          selectedKeys={[field.value]}
                          onSelectionChange={(keys) => {
                            const v = Array.from(keys as Set<string>)[0];
                            if (v) field.onChange(v as TimeframePreference);
                          }}
                          isInvalid={Boolean(errors.timeframePreference)}
                          errorMessage={errors.timeframePreference?.message}
                          aria-label={t('quote.timeframe_label', 'Timeframe')}
                          className="text-xs"
                          size="sm"
                          isRequired
                          classNames={{
                            trigger:
                              'border !border-gray-300 rounded-md !bg-white hover:!border-gray-300 focus-within:!border-gray-300 focus-within:!bg-white outline-none focus:outline-none',
                            value: 'text-xs',
                            listbox: 'text-xs',
                          }}
                        >
                          {TIMEFRAME_OPTIONS.map((opt) => (
                            <SelectItem className="text-xs" key={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </Select>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-body font-medium text-gray-700 block mb-1.5">
                        {t('quote.budget_min', 'Budget min')}
                      </label>
                      <Input
                        {...register('budgetMin')}
                        type="number"
                        label="$"
                        labelPlacement="outside-left"
                        placeholder="0"
                        isInvalid={Boolean(errors.budgetMin)}
                        errorMessage={errors.budgetMin?.message}
                        aria-label={t('quote.budget_min', 'Budget min')}
                        size="sm"
                        variant="bordered"
                        classNames={{
                          input: 'text-xs bg-white',
                          inputWrapper:
                            'border border-gray-300 rounded-md text-xs data-[hover=true]:!border-gray-300 data-[hover=true]:!bg-white group-data-[focus=true]:!border-gray-300 focus-within:!border-gray-300',
                        }}
                      />
                    </div>
                    <div>
                      <label className="text-body font-medium text-gray-700 block mb-1.5">
                        {t('quote.budget_max', 'Budget max')}
                      </label>
                      <Input
                        {...register('budgetMax')}
                        type="number"
                        label="$"
                        labelPlacement="outside-left"
                        placeholder="0"
                        isInvalid={Boolean(errors.budgetMax)}
                        errorMessage={errors.budgetMax?.message}
                        aria-label={t('quote.budget_max', 'Budget max')}
                        size="sm"
                        variant="bordered"
                        classNames={{
                          input: 'text-xs bg-white',
                          inputWrapper:
                            'border border-gray-300 rounded-md text-xs data-[hover=true]:!border-gray-300 data-[hover=true]:!bg-white group-data-[focus=true]:!border-gray-300 focus-within:!border-gray-300',
                        }}
                      />
                    </div>
                  </div>
                </div>

                <Divider />

                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-caption text-gray-500">
                    {t('quote.no_charge_note', 'No charge to request a quote.')}
                  </p>
                  <CustomButton
                    type="submit"
                    label={t('quote.submit', 'Submit Request')}
                    color="primary"
                    size="md"
                    startContent={<FaPaperPlane />}
                    isLoading={createJobMutation.isPending}
                  />
                </div>
              </CardBody>
            </Card>
          </form>

          {/* Enhanced Service Card */}
          <div>
            <p className="text-caption text-gray-500 font-md uppercase tracking-wide mb-1">
              {t('quote.requesting_quote_for', 'Requesting a quote for')}
            </p>
            <Card
              shadow="sm"
              className="border border-gray-200 lg:sticky lg:top-6 overflow-hidden"
            >
              {isServiceLoading ? (
                <div className="p-6 space-y-4 animate-pulse">
                  <div className="h-48 rounded-lg bg-gray-100" />
                  <div className="space-y-3">
                    <div className="h-4 w-3/4 rounded bg-gray-100" />
                    <div className="h-3 w-1/2 rounded bg-gray-100" />
                    <div className="h-3 w-2/3 rounded bg-gray-100" />
                  </div>
                </div>
              ) : requestedService ? (
                <>
                  <div className="relative">
                    <img
                      src={
                        requestedService.gallery?.[0]?.serviceImages?.[0] ||
                        'https://via.placeholder.com/400x300/eee/999?text=No+Image'
                      }
                      alt={requestedService.serviceTitle || 'Requested service'}
                      className="w-full aspect-[4/3] object-cover"
                    />
                  </div>

                  <CardBody className="p-3 space-y-2">
                    {/* Category chip - moved above service title */}
                    {(requestedService.categoryName ||
                      requestedService.category?.categoryName ||
                      requestedService.category) && (
                      <div>
                        <CustomChip
                          label={
                            requestedService.categoryName ||
                            requestedService.category?.categoryName ||
                            (typeof requestedService.category === 'string'
                              ? requestedService.category
                              : '')
                          }
                          size="sm"
                          variant="flat"
                          color="primary"
                          className="text-md rounded-full"
                        />
                      </div>
                    )}

                    {/* Service title and subtitle */}
                    <div>
                      <h2 className="text-title2 font-bold text-gray-900 leading-tight">
                        {requestedService.serviceTitle || 'Service'}
                      </h2>
                    </div>

                    {/* Pricing type badge */}
                    <div className="flex items-center gap-1 flex-wrap">
                      <CustomChip
                        label={t(
                          'quote.quote_based_pricing',
                          'Quote-Based Pricing'
                        )}
                        size="md"
                        variant="flat"
                        color="primary"
                        customClassNames={{
                          base: 'px-2 bg-primary items-center rounded-full',
                          content:
                            'text-sm font-semibold text-white bg-primary',
                        }}
                      />
                    </div>

                    {/* Provider response SLA — Item 1. Shown whenever a
                        response-time is advertised (or the 2-day fallback). */}
                    {responseSlaDays > 0 && (
                      <div className="flex items-center gap-2 text-body text-gray-600">
                        <FaRegClock
                          className="text-secondary shrink-0"
                          size={14}
                        />
                        <span>
                          {t(
                            'quote.responds_within_days',
                            'This provider usually responds within {{count}} days',
                            { count: responseSlaDays }
                          )}
                        </span>
                      </div>
                    )}

                    {/* Provider's quote terms — rendered when available */}
                    {requestedService?.quoteDetails && (
                      <div className="pt-2 border-t border-gray-100">
                        <p className="text-xs font-medium text-gray-500 mb-2">
                          {t(
                            'quote.provider_quote_terms',
                            "Provider's quote terms"
                          )}
                        </p>
                        <p className="text-sm text-gray-700 whitespace-pre-line">
                          {requestedService.quoteDetails}
                        </p>
                      </div>
                    )}

                    <Divider />

                    {/* Service details */}
                    <div className="space-y-2 text-body text-gray-600">
                      {requestedService.location?.[0] && (
                        <div className="flex items-start gap-3">
                          <FaMapLocationDot
                            className="text-secondary flex-shrink-0 mt-1"
                            size={14}
                          />
                          <span>
                            {[
                              requestedService.location[0].addressLine,
                              requestedService.location[0].city,
                              requestedService.location[0].state,
                            ]
                              .filter(Boolean)
                              .join(', ')}
                          </span>
                        </div>
                      )}
                    </div>
                  </CardBody>
                </>
              ) : (
                <CardBody className="p-6 text-center">
                  <p className="text-body text-gray-500">
                    {t(
                      'quote.service_unavailable',
                      'Service details are currently unavailable.'
                    )}
                  </p>
                </CardBody>
              )}
            </Card>
          </div>
        </div>
      </FormProvider>

      {createJobMutation.isError && (
        <div className="mt-6">
          <CustomAlert
            title="Could not submit your request"
            description={
              (createJobMutation.error as any)?.message ||
              'Please try again. If the problem persists, contact support.'
            }
            color="danger"
          />
        </div>
      )}
    </div>
  );
};

export default JobRequestForm;
